import { DEFAULT_CONFIG, DEFAULT_OVERRIDES } from './defaults.js';
import { resolveApiError } from './error-resolve.js';
export function createErrorHandler(options = {}) {
    const config = { ...DEFAULT_CONFIG, ...options.config };
    const overrides = options.overrides ?? {};
    const callbacks = options.callbacks ?? {};
    const storage = options.storage;
    const errorLog = [];
    const handleApiError = (error, context = 'Unknown') => {
        const resolved = resolveApiError(error, overrides);
        const apiError = {
            message: resolved.message,
            status: resolved.status,
            code: resolved.code,
            isAuthError: resolved.isAuthError,
            context,
            timestamp: Date.now(),
        };
        if (config.skipAuthErrors && apiError.isAuthError) {
            return apiError;
        }
        logError(apiError, resolved.category, config, callbacks, errorLog);
        callbacks.reportError?.(error, context);
        if (config.logToStorage && storage) {
            storage.persist(apiError, config.maxStoredErrors);
        }
        if (config.showUserNotifications && resolved.notification) {
            notifyUser(resolved.notification, mergedAuthMessage(overrides), callbacks);
        }
        return apiError;
    };
    return {
        handleApiError,
        getErrorLog: () => [...errorLog],
        getStoredErrors: () => storage?.getStored() ?? [],
        clearErrorLog: () => {
            errorLog.length = 0;
            storage?.clear();
        },
        hasCriticalErrors: (timeWindowMs = 5 * 60 * 1000) => {
            const cutoffTime = Date.now() - timeWindowMs;
            return errorLog.some((entry) => entry.timestamp > cutoffTime && entry.status !== undefined && entry.status >= 500);
        },
    };
}
function mergedAuthMessage(overrides) {
    return overrides.authEventMessage ?? DEFAULT_OVERRIDES.authEventMessage;
}
function logError(apiError, category, config, callbacks, errorLog) {
    if (!config.logToConsole) {
        return;
    }
    const logMessage = `API Error [${apiError.context}]:`;
    const errorDetails = {
        message: apiError.message,
        status: apiError.status,
        code: apiError.code,
        timestamp: new Date(apiError.timestamp).toISOString(),
    };
    const log = callbacks.log ??
        ((level, message, details) => {
            if (level === 'error') {
                console.error(message, details);
                return;
            }
            if (level === 'warn') {
                console.warn(message, details);
                return;
            }
            console.log(message, details);
        });
    if (category === 'server' || (apiError.status !== undefined && apiError.status >= 500)) {
        log('error', logMessage, errorDetails);
    }
    else if (category === 'client' || (apiError.status !== undefined && apiError.status >= 400)) {
        log('warn', logMessage, errorDetails);
    }
    else {
        log('log', logMessage, errorDetails);
    }
    errorLog.push(apiError);
    if (errorLog.length > 100) {
        errorLog.splice(0, errorLog.length - 50);
    }
}
function notifyUser(notification, authMessage, callbacks) {
    if (notification.kind === 'auth') {
        callbacks.onAuthError?.({ message: authMessage });
        return;
    }
    callbacks.onApiError?.({ title: notification.title, message: notification.message });
}
